/**
 * Pedidos — **gestão centralizada multicanal** (Task 12, brief 3.3) e, ao
 * mesmo tempo, o drill-down dos indicadores de pedido/faturamento (Task 10).
 *
 * Leia isto antes de mexer:
 *
 * 1. **A lista não tem filtro próprio.** Com `?kpi=` ela pede os registros ao
 *    MESMO seletor que calculou o card (`ordersForKpi` → `selectKpiRecords`) e
 *    traduz aqueles ids de pedido do checkout para as linhas unificadas pelo
 *    vínculo `checkoutOrderId`. Nenhum recorte é recalculado aqui.
 * 2. **A origem é preservada e navegável.** Cada linha mostra o canal e o
 *    identificador externo e leva ao registro de origem (conversa do
 *    atendimento, pedido do checkout) — não só a um rótulo "WhatsApp".
 * 3. **O indicador conta o que ele conta.** Os KPIs de pedido da Task 10 são
 *    calculados sobre `/api/checkout/orders` (Loja). Quando a lista está
 *    filtrada por indicador, ela DIZ quantos pedidos de outros canais ficam
 *    fora daquele número, em vez de deixar o operador concluir que a Loja é a
 *    operação inteira (D-12.7).
 * 4. **Fonte unificada ausente não é "nenhum pedido".** Sem
 *    `/api/platform/orders` a tela cai no recorte do checkout e declara a
 *    ausência; quando o ambiente publica a rota, a lista passa a multicanal
 *    sozinha.
 */
import { createElement as h, useState, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PeriodOrdersBoundary } from "../components/PeriodOrdersBoundary.js";
import { OrderKanban } from "../components/OrderKanban.js";
import { productSummary } from "../orders/products.js";
import { ShoppingBag } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { DrillDownHeader } from "../components/DrillDownHeader.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { Badge, Button, Card, Drawer, EmptyState, Field, Segmented, buttonClass, controlClass } from "../ui/index.js";
import { salesChannelBuckets, salesChannelLabel, salesChannelKey } from "../orders/channels.js";
import { acquisitionBuckets, type SalesOriginOption } from "../orders/acquisition.js";
import { useSalesOrigins } from "../queries.js";
import { EMPTY_ORDER_FILTERS, filterOrders, type OrderFilters } from "../orders/list-view.js";
import { ordersForKpi, parseKpiId } from "../dashboard/drilldown.js";
import {
  isOrdersSourceAwaitingContract,
  originDrillDown,
  ordersForCheckoutIds,
  ordersOutsideCheckout
} from "../orders/dataset.js";
import {
  ORDER_ORIGINS,
  ORDER_ORIGIN_LABELS,
  ORDER_STATES,
  ORDER_STATE_LABELS,
  type PlatformOrderOrigin,
  type PlatformOrderState
} from "../orders/state-machine.js";
import { AWAITING_CONTRACT_LABEL, type DashboardDataset } from "../dashboard/kpis.js";
import { formatBrl, formatDateTime, formatOrderStatus } from "../format.js";
import type { OrdersDataset } from "../orders/dataset.js";
import type { PedidosViewProps } from "../orders/view-props.js";
import type { PlatformOrder, PlatformUnifiedOrder } from "../api/types.js";
import type { BadgeTone } from "../ui/index.js";

/** Tom do selo por canal e por estado: sempre acompanhado do texto (cor nunca é o único sinal). */
const ORIGIN_TONE: Record<PlatformOrderOrigin, BadgeTone> = { loja: "neutral", whatsapp: "success", marketplace: "brand", manual: "info" };
/** Rótulo curto do canal na tabela (o nome completo fica no `title` e nos filtros). */
const ORIGIN_SHORT: Record<PlatformOrderOrigin, string> = { loja: "Loja", whatsapp: "WhatsApp", marketplace: "Marketplace", manual: "Manual" };
const STATE_TONE: Record<PlatformOrderState, BadgeTone> = {
  payment_pending: "warning",
  paid: "info",
  separation: "info",
  shipping: "info",
  invoiced: "info",
  delivered: "success",
  cancelled: "danger",
  returned: "danger"
};

/**
 * Recorte do checkout da Loja — o drill-down dos indicadores da Task 10.
 * Continua existindo (e com as mesmas colunas) porque é o fallback honesto
 * quando a fonte unificada não está publicada no ambiente.
 */
export function PedidosCheckoutTable({ orders }: { orders: PlatformOrder[] }): ReactElement {
  return h(RecordTable<PlatformOrder>, {
    caption: "Pedidos do checkout da Loja que compõem o indicador selecionado",
    rows: orders,
    idOf: (order) => order.id,
    emptyMessage: "Nenhum pedido compõe este indicador no momento.",
    // Fase 8 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
    animateRows: true,
    columns: [
      { key: "id", label: "Pedido", render: (order) => order.id },
      { key: "placedAt", label: "Data", render: (order) => formatDateTime(order.placedAt) },
      { key: "status", label: "Status", render: (order) => formatOrderStatus(order.status) },
      {
        key: "total",
        label: "Total",
        render: (order) => (order.totalCents === null ? "aguardando cotação de frete" : formatBrl(order.totalCents))
      },
      { key: "customer", label: "Cliente", render: (order) => order.customer?.name ?? "—" },
      {
        key: "payments",
        label: "Pagamentos",
        render: (order) =>
          order.payments.length
            ? order.payments.map((payment) => `${payment.method}:${payment.status}`).join(", ")
            : "nenhum"
      }
    ]
  });
}

/**
 * Lista unificada: pedidos da Loja, vendas do WhatsApp e outros canais no
 * MESMO lugar, com origem, identificador externo, estado da máquina e
 * navegação para o registro de origem e para o detalhe do pedido.
 */
export function PedidosUnificadosTable({
  orders,
  onOpen
}: {
  orders: PlatformUnifiedOrder[];
  /** Quando presente, cada linha ganha o botão que abre o painel de resumo. */
  onOpen?: (order: PlatformUnifiedOrder) => void;
}): ReactElement {
  return h(RecordTable<PlatformUnifiedOrder>, {
    caption: "Pedidos de todos os canais, com origem e identificador externo preservados",
    rows: orders,
    idOf: (order) => order.id,
    emptyMessage: "Nenhum pedido neste recorte.",
    animateRows: true,
    columns: [
      {
        key: "id",
        label: "Pedido",
        render: (order) =>
          h(
            Link,
            { to: `/pedidos/${order.id}`, className: "font-black text-ink underline decoration-brand decoration-2 underline-offset-2" },
            order.id
          )
      },
      { key: "placedAt", label: "Data", render: (order) => h("span", { className: "text-xs text-muted" }, formatDateTime(order.placedAt)) },
      {
        key: "origin",
        label: "Canal",
        render: (order) =>
          h(
            Badge,
            { tone: ORIGIN_TONE[order.origin], title: order.channelName ?? ORDER_ORIGIN_LABELS[order.origin], "data-order-origin": order.origin, "data-order-channel": salesChannelKey(order) },
            order.channelKind ? salesChannelLabel(order.channelKind) : ORIGIN_SHORT[order.origin]
          )
      },
      {
        key: "externalId",
        label: "Identificador no canal",
        render: (order) => {
          // O TEXTO desta célula é sempre `externalId` — o identificador da
          // venda no canal, que é o que o operador tem em mãos ao procurar o
          // pedido. O drill-down vira o LINK desse texto: o id interno do
          // registro de origem (conversa do atendimento, pedido do checkout)
          // nunca substitui o identificador do canal na fila (finding da
          // revisão da Task 12).
          const drill = originDrillDown(order);
          return h(
            "span",
            { "data-order-external-id": order.externalId },
            drill.route
              ? h(Link, { to: drill.route, title: drill.label, "aria-label": `${order.externalId}: ${drill.label}` }, order.externalId)
              : order.externalId
          );
        }
      },
      { key: "customer", label: "Cliente", render: (order) => order.customer?.name ?? "—" },
      {
        key: "product",
        label: "Produto vendido",
        render: (order) => {
          const product = productSummary(order);
          return h(
            "span",
            { className: "flex flex-col", "data-order-product": product.first },
            h("span", { className: "font-semibold text-ink" }, product.first),
            product.more > 0 ? h("span", { className: "text-xs text-muted" }, `+ ${product.more} outro(s) item(ns)`) : null
          );
        }
      },
      {
        key: "total",
        label: "Total",
        align: "right" as const,
        render: (order) =>
          order.totalCents === null
            ? "aguardando cotação de frete"
            : h("span", { className: "font-extrabold tabular-nums" }, formatBrl(order.totalCents))
      },
      {
        key: "state",
        label: "Estado",
        render: (order) =>
          h(
            "span",
            { className: "flex flex-col items-start gap-0.5" },
            h(Badge, { tone: STATE_TONE[order.state], "data-order-state": order.state }, ORDER_STATE_LABELS[order.state]),
            order.blingSituation
              ? h("span", { className: "text-xs text-muted", "data-bling-situation": order.blingSituation, title: "Situação no Bling" }, `Bling: ${order.blingSituation}`)
              : null
          )
      },
      ...(onOpen
        ? [
            {
              key: "actions",
              label: "",
              render: (order: PlatformUnifiedOrder) =>
                h(
                  Button,
                  {
                    size: "sm" as const,
                    onClick: () => onOpen(order),
                    "aria-label": `Abrir resumo do pedido ${order.id}`,
                    "data-order-open": order.id
                  },
                  "Resumo"
                )
            }
          ]
        : [])
    ]
  });
}

/** Conteúdo do painel lateral: o resumo do pedido sem sair da lista. */
export function PedidoResumo({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  const row = (label: string, value: string): ReactElement =>
    h(
      "div",
      { key: label, className: "flex justify-between gap-3 border-b border-border py-2 text-sm" },
      h("dt", { className: "text-muted" }, label),
      h("dd", { className: "m-0 text-right font-semibold text-ink" }, value)
    );
  return h(
    "div",
    { className: "flex flex-col gap-4", "data-order-summary": order.id },
    h(
      "dl",
      { className: "m-0" },
      row("Estado", ORDER_STATE_LABELS[order.state]),
      row("Origem", ORDER_ORIGIN_LABELS[order.origin]),
      row("Canal de venda", order.channelName ?? salesChannelLabel(salesChannelKey(order))),
      row("Situação no Bling", order.blingSituation ?? "—"),
      row("Identificador no canal", order.externalId),
      row("Cliente", order.customer?.name ?? "—"),
      row("Data", formatDateTime(order.placedAt)),
      row("Subtotal", formatBrl(order.subtotalCents)),
      row("Frete", order.shippingCents === null ? "aguardando cotação" : formatBrl(order.shippingCents)),
      row("Total", order.totalCents === null ? "aguardando cotação de frete" : formatBrl(order.totalCents)),
      row("NF (Bling)", order.nfReference ?? "—"),
      row("Transportadora", order.carrier ?? "—"),
      row("Rastreio", order.trackingCode ?? "—")
    ),
    h("p", { className: "text-sm text-muted" }, order.stateNote),
    h(
      "div",
      null,
      h("h3", { className: "mb-2 text-sm font-extrabold text-ink" }, `Itens (${order.items.length})`),
      order.items.length
        ? h(
            "ul",
            { className: "m-0 flex list-none flex-col gap-1 p-0 text-sm" },
            order.items.map((item) =>
              h(
                "li",
                { key: `${item.productId}-${item.offerId}`, className: "flex justify-between gap-3" },
                h("span", null, `${item.quantity}× ${(item as { description?: string }).description || item.productId}`),
                h("span", { className: "tabular-nums" }, formatBrl(item.unitPriceCents * item.quantity))
              )
            )
          )
        : h("p", { className: "text-sm text-muted" }, "Sem itens registrados.")
    ),
    h(Link, { to: `/pedidos/${order.id}`, className: buttonClass("primary", "md", "w-fit") }, "Abrir pedido completo")
  );
}

const ORDERS_PAGE_SIZE = 100;

/** Chip de filtro (estado): ativo = grafite preenchido, como no admin de referência. */
function chipClass(active: boolean): string {
  return (
    "min-h-10 rounded-pill border px-3.5 text-sm font-extrabold tabular-nums transition-colors " +
    (active ? "border-ink bg-ink text-white" : "border-border-strong bg-surface text-ink hover:border-ink")
  );
}

/**
 * Lista com filtros (busca, canal, estado) e painel lateral. Os filtros são só
 * de apresentação sobre o recorte que a página já decidiu (`?kpi=`/`?checkout=`).
 */
export function PedidosLista({ orders, initialChannel, initialAcquisition, acquisitionOptions }: { orders: PlatformUnifiedOrder[]; initialChannel?: string; initialAcquisition?: string; acquisitionOptions?: readonly SalesOriginOption[] }): ReactElement {
  const [filters, setFilters] = useState<OrderFilters>({ ...EMPTY_ORDER_FILTERS, channel: initialChannel || "todos", acquisition: initialAcquisition || "todos" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Com a carga real (milhares de pedidos) a tabela mostra a página corrente; o filtro vale para tudo.
  const [shown, setShown] = useState(ORDERS_PAGE_SIZE);
  const [mode, setMode] = useState<"lista" | "kanban">("lista");
  const visible = filterOrders(orders, filters);
  const selected = orders.find((order) => order.id === selectedId) ?? null;
  const channelOptions = salesChannelBuckets(orders);
  const acquisitionChoices = acquisitionBuckets(orders, { options: acquisitionOptions });
  const filtering = filters.query !== "" || filters.origin !== "todos" || (filters.channel ?? "todos") !== "todos" || (filters.acquisition ?? "todos") !== "todos" || filters.state !== "todos";

  return h(
    "div",
    { className: "flex flex-col gap-4", "data-orders-list": String(visible.length) },
    h(
      "div",
      { className: "flex flex-wrap items-center justify-between gap-3" },
      h(Segmented<"lista" | "kanban">, {
        label: "Modo de visualização",
        value: mode,
        options: [{ value: "lista", label: "Lista" }, { value: "kanban", label: "Kanban por estado" }],
        onChange: setMode
      }),
      h("span", { className: "text-sm text-muted tabular-nums", "data-orders-count": String(visible.length) }, `${visible.length} de ${orders.length} pedido(s) no período`)
    ),
    h(
      Card,
      { "aria-label": "Filtros de pedidos" },
      h(
        "div",
        { className: "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr]" },
        h(Field, {
          label: "Buscar por número, cliente ou produto",
                    children: ({ id, describedBy, invalid }) =>
            h("input", {
              id,
              type: "search",
              value: filters.query,
              "aria-describedby": describedBy,
              "aria-invalid": invalid,
              "data-filter-control": "query",
              placeholder: "Pedido, cliente, produto ou SKU",
              className: controlClass,
              onChange: (event: { target: { value: string } }) => setFilters({ ...filters, query: event.target.value })
            })
        }),
        h(Field, {
          label: "Canal de venda",
          children: ({ id }) =>
            h(
              "select",
              {
                id,
                value: filters.channel ?? "todos",
                "data-filter-control": "channel",
                className: controlClass,
                onChange: (event: { target: { value: string } }) => setFilters({ ...filters, channel: event.target.value })
              },
              h("option", { value: "todos" }, "Todos os canais de venda"),
              channelOptions.map((bucket) => h("option", { key: bucket.key, value: bucket.key }, `${bucket.label} (${bucket.count})`))
            )
        }),
        h(Field, {
          label: "Origem do contato",
          children: ({ id }) =>
            h(
              "select",
              {
                id,
                value: filters.acquisition ?? "todos",
                "data-filter-control": "acquisition",
                className: controlClass,
                onChange: (event: { target: { value: string } }) => setFilters({ ...filters, acquisition: event.target.value })
              },
              h("option", { value: "todos" }, "Todas as origens do contato"),
              acquisitionChoices.map((bucket) => h("option", { key: bucket.key, value: bucket.key }, `${bucket.label} (${bucket.orders})`))
            )
        }),
        h(Field, {
          label: "Origem",
          children: ({ id }) =>
            h(
              "select",
              {
                id,
                value: filters.origin,
                "data-filter-control": "origin",
                className: controlClass,
                onChange: (event: { target: { value: string } }) =>
                  setFilters({ ...filters, origin: event.target.value as PlatformOrderOrigin | "todos" })
              },
              h("option", { value: "todos" }, "Todos os canais"),
              ORDER_ORIGINS.map((origin) => h("option", { key: origin, value: origin }, ORDER_ORIGIN_LABELS[origin]))
            )
        }),
      ),
      h(
        "div",
        { role: "group", "aria-label": "Filtrar por estado", "data-filter-control": "state", className: "mt-4 flex flex-wrap gap-2" },
        h(
          "button",
          {
            type: "button",
            "aria-pressed": filters.state === "todos",
            onClick: () => setFilters({ ...filters, state: "todos" }),
            className: chipClass(filters.state === "todos")
          },
          `Todos ${orders.length}`
        ),
        ORDER_STATES.map((state) => {
          const count = orders.filter((order) => order.state === state).length;
          return h(
            "button",
            {
              key: state,
              type: "button",
              "aria-pressed": filters.state === state,
              onClick: () => setFilters({ ...filters, state: filters.state === state ? "todos" : state }),
              className: chipClass(filters.state === state)
            },
            `${ORDER_STATE_LABELS[state]} ${count}`
          );
        })
      ),
      filtering
        ? h(
            "div",
            { className: "mt-3 flex items-center gap-3 text-sm text-muted" },
            h("span", null, `${visible.length} de ${orders.length} pedido(s)`),
            h(Button, { size: "sm", variant: "ghost", onClick: () => setFilters(EMPTY_ORDER_FILTERS) }, "Limpar filtros")
          )
        : null
    ),
    visible.length === 0 && orders.length > 0
      ? h(EmptyState, { title: "Nenhum pedido encontrado", description: "Ajuste os filtros para ver outros pedidos." })
      : mode === "kanban"
        ? h(OrderKanban, { orders: visible })
        : h(PedidosUnificadosTable, { orders: visible.slice(0, shown), onOpen: (order) => setSelectedId(order.id) }),
    mode === "lista" && visible.length > shown
      ? h(
          "div",
          { className: "flex items-center gap-3 text-sm text-muted", "data-orders-more": String(visible.length - shown) },
          h("span", null, `Mostrando ${shown} de ${visible.length} pedido(s)`),
          h(Button, { size: "sm", onClick: () => setShown(shown + ORDERS_PAGE_SIZE) }, "Mostrar mais")
        )
      : null,
    h(Drawer, {
      open: selected !== null,
      title: selected ? `Pedido ${selected.id}` : "Pedido",
      onClose: () => setSelectedId(null),
      children: selected ? h(PedidoResumo, { order: selected }) : null
    })
  );
}

/** Composição por canal — o que a lista unificada contém, declarado. */
function OriginSummary({ orders }: { orders: OrdersDataset }): ReactElement {
  return h(
    "p",
    { className: "page__note flex items-center gap-2 text-sm text-muted", "data-origin-summary": orders.orders.length },
    h(ShoppingBag, { className: "h-4 w-4 shrink-0 text-brand-text", "aria-hidden": true, focusable: false }),
    "Composição por canal: ",
    salesChannelBuckets(orders.orders)
      .map((bucket) => `${bucket.label}: ${bucket.count}`)
      .join(" · ")
  );
}

export function PedidosView({ dataset, kpiId, now, orders, checkoutOrderId = null, initialChannel, initialAcquisition, acquisitionOptions, toolbar }: PedidosViewProps): ReactElement {
  const checkoutOrders = ordersForKpi(dataset, kpiId, now ?? new Date());

  // Fonte unificada não carregada/publicada: recorte do checkout, declarado
  // como tal. Nunca apresentamos a Loja como se fosse todos os canais.
  if (!orders || isOrdersSourceAwaitingContract(orders)) {
    return h(
      "div",
      { className: "page page--pedidos flex flex-col gap-6", "data-page": "pedidos" },
      h(DrillDownHeader, { title: "Pedidos", kpiId, recordCount: checkoutOrders.length, recordNoun: "pedido(s)" }),
      orders
        ? h(StatusPanel, {
            kind: "awaiting_source",
            title: `Pedidos unificados multicanal ${AWAITING_CONTRACT_LABEL}`,
            detail:
              "A rota /api/platform/orders não respondeu neste ambiente. A lista abaixo é apenas o checkout da Loja — " +
              "vendas do WhatsApp e de outros canais não aparecem aqui até a rota ser publicada."
          })
        : null,
      h(PedidosCheckoutTable, { orders: checkoutOrders })
    );
  }

  // Recorte: por indicador (`?kpi=`), por pedido do checkout (`?checkout=`,
  // usado pelo drill-down de origem) ou a lista inteira.
  const restricted = kpiId
    ? ordersForCheckoutIds(orders, checkoutOrders.map((order) => order.id))
    : checkoutOrderId
      ? ordersForCheckoutIds(orders, [checkoutOrderId])
      : orders.orders;
  const outsideCheckout = ordersOutsideCheckout(orders);

  return h(
    "div",
    { className: "page page--pedidos flex flex-col gap-6", "data-page": "pedidos" },
    h(DrillDownHeader, { title: "Pedidos", kpiId, recordCount: restricted.length, recordNoun: "pedido(s)" }),
    toolbar ?? null,
    // Avisos de escopo/kpi num cartão tokenizado (padrão aprovado): o recorte
    // do indicador e o aviso de origem andam juntos num bloco, com a nota de
    // composição por canal e a nota fiscal logo abaixo. Os data-*/texto são
    // preservados — só o container e as classes de texto mudam.
    h(
      "section",
      { className: "rounded-panel border border-border bg-surface p-4 shadow-s1 flex flex-col gap-2" },
      h(OriginSummary, { orders }),
      kpiId
        ? h(
            "p",
            { className: "page__note text-sm text-muted", "data-kpi-scope": "checkout", "data-orders-outside-kpi": outsideCheckout.length },
            `Este indicador é calculado sobre os pedidos do checkout da Loja (/api/checkout/orders). ` +
              `${outsideCheckout.length} pedido(s) de outros canais estão na operação e NÃO entram neste número — ` +
              `veja a lista completa em /pedidos.`
          )
        : null,
      checkoutOrderId && !kpiId
        ? h(
            "p",
            { className: "page__note text-sm text-muted", "data-checkout-scope": checkoutOrderId },
            `Recorte do pedido do checkout ${checkoutOrderId}: o registro unificado que o projeta.`
          )
        : null,
      h(
        "p",
        { className: "page__note text-sm text-muted" },
        "Pedido da Loja não é recadastrado aqui: o registro unificado referencia o pedido do checkout " +
          "(mesmos registros, mesmo histórico). Faturamento fiscal permanece no Bling — a Plataforma guarda a referência da NF."
      )
    ),
    h(PedidosLista, { orders: restricted, initialChannel, initialAcquisition, acquisitionOptions })
  );
}

export function Pedidos(): ReactElement {
  const [searchParams] = useSearchParams();
  const kpiId = parseKpiId(searchParams.get("kpi"));
  const checkoutOrderId = searchParams.get("checkout");
  const initialChannel = searchParams.get("canal") ?? undefined;
  const initialAcquisition = searchParams.get("origem") ?? undefined;
  const origins = useSalesOrigins();
  return h(DatasetBoundary, {
    children: (dataset: DashboardDataset) =>
      // Padrão: ESTA SEMANA. O período vai ao servidor; a lista não carrega o histórico inteiro.
      h(PeriodOrdersBoundary, {
        defaultPeriod: "semana",
        children: (orders: OrdersDataset, context) => h(PedidosView, { dataset, kpiId, orders, checkoutOrderId, initialChannel, initialAcquisition, acquisitionOptions: origins.data, toolbar: context.toolbar })
      })
  });
}
